import cornerstoneTools from "cornerstone-tools";
import Util from "../../api/Util";
import {
  getDimensionData,
  getTextBoxAnchorPoints,
} from "../measurementToolUtils";
import { ImageMetadata } from "pages/viewer/dicomViewer.types";
import { TOOL_IDS } from "../../consts/tools.consts";
import Drawing from "../../api/Drawing";
import toolStyle from "../../api/state-management/toolStyle";
import cornerstone, { triggerEvent } from "cornerstone-core";
import toolColors from "../../api/state-management/toolColours";
import { DEFAULT_HANDLE } from "../../consts/tools.defaults";
import Manipulators from "../../api/Manipulators";
import { ExtendedAnnotationTool } from "../../api/ExtendedAnnotationTool";
import { ToolData } from "../../tools.types";

const DEGREES_SYMBOL = "00B0";
const ANGLE_CONFIG = {
  drawHandles: true,
  drawHandlesOnHover: false,
  hideHandlesIfMoving: false,
  renderDashed: false,
};

export default class AngleTool extends ExtendedAnnotationTool {
  preventNewMeasurement: boolean;
  constructor() {
    super(TOOL_IDS.ANGLE);
    this.preventNewMeasurement = false;
  }

  createNewMeasurement(eventData) {
    // Create the measurement data for this tool with the end handle activated
    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        middle: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
    };
  }

  pointNearTool(element, data, coords) {
    if (data.visible === false) {
      return false;
    }

    return (
      Util.lineSegDistance(
        element,
        data.handles.start,
        data.handles.middle,
        coords
      ) < 25 ||
      Util.lineSegDistance(
        element,
        data.handles.middle,
        data.handles.end,
        coords
      ) < 25
    );
  }

  updateCachedStats(image, _element, data) {
    const sideA = getSide(
      image,
      data.handles.middle,
      data.handles.start,
      this.imageMetaData
    );
    const sideB = getSide(
      image,
      data.handles.end,
      data.handles.middle,
      this.imageMetaData
    );
    const sideC = getSide(
      image,
      data.handles.end,
      data.handles.start,
      this.imageMetaData
    );

    const sideALength = length(sideA);
    const sideBLength = length(sideB);
    const sideCLength = length(sideC);

    // Cosine law
    let angle = Math.acos(
      (Math.pow(sideALength, 2) +
        Math.pow(sideBLength, 2) -
        Math.pow(sideCLength, 2)) /
        (2 * sideALength * sideBLength)
    );

    const { rowPixelSpacing, colPixelSpacing } = getDimensionData(
      image,
      this.imageMetaData
    );
    data.suffix = !rowPixelSpacing || !colPixelSpacing ? " (isotropic)" : "";

    angle *= 180 / Math.PI;

    data.rAngle = Util.roundToDecimal(angle, 2);
    data.invalidated = false;
  }

  drawToolData(
    element: HTMLElement,
    context: CanvasRenderingContext2D,
    toolData: ToolData
  ): void {
    const { drawHandles, drawHandlesOnHover, hideHandlesIfMoving } =
      ANGLE_CONFIG;
    const { visible, handles, rAngle, suffix } = toolData;
    if (visible !== false) {
      Drawing.draw(context, (context) => {
        Drawing.setShadow(context, ANGLE_CONFIG);

        // Differentiate the color of activation tool
        const color = toolColors.getColorIfActive(toolData);

        const handleStartCanvas = cornerstone.pixelToCanvas(
          element,
          handles.start
        );
        const handleMiddleCanvas = cornerstone.pixelToCanvas(
          element,
          handles.middle
        );

        const lineOptions = { color };

        Drawing.drawJoinedLines(
          context,
          element,
          handles.start,
          [handles.middle, handles.end],
          lineOptions
        );

        // Draw the handles
        const handleOptions = {
          ...DEFAULT_HANDLE,
          color,
          drawHandlesIfActive: drawHandlesOnHover,
          hideHandlesIfMoving,
        };

        if (drawHandles) {
          Drawing.drawHandles(context, { element }, handles, handleOptions);
        }

        if (rAngle) {
          const text = `${rAngle.toString()} ${String.fromCharCode(
            parseInt(DEGREES_SYMBOL, 16)
          )} ${suffix ?? ""}`;
          const distance = 15;

          let textCoords;

          if (!handles.textBox.hasMoved) {
            textCoords = {
              x: handleMiddleCanvas.x,
              y: handleMiddleCanvas.y,
            };

            const padding = 5;
            const textWidth = Drawing.textBoxWidth(context, text, padding);

            if (handleMiddleCanvas.x < handleStartCanvas.x) {
              textCoords.x -= distance + textWidth + 10;
            } else {
              textCoords.x += distance;
            }

            handles.textBox.x = handles.middle.x + 10;
            handles.textBox.y = handles.middle.y + 10;
          }
          const lineWidth = toolStyle.getToolWidth();

          Drawing.drawLinkedTextBox(
            context,
            element,
            handles.textBox,
            text,
            handles,
            getTextBoxAnchorPoints,
            color,
            lineWidth,
            0,
            true
          );
        }
      });
    }
  }

  addNewMeasurement(evt, interactionType) {
    if (this.preventNewMeasurement) {
      return;
    }

    console.log("test");

    this.preventNewMeasurement = true;
    evt.preventDefault();
    evt.stopPropagation();

    const eventData = evt.detail;
    console.log(eventData);
    const measurementData = this.createNewMeasurement(eventData);
    const element = evt.detail.element;

    // Associate this data with this imageId so we can render it and manipulate it
    cornerstoneTools.addToolState(element, this.toolId, measurementData);
    cornerstone.updateImage(element);

    // Step 1, create start and second middle.
    Manipulators.moveNewHandle(
      eventData,
      this.toolId,
      measurementData,
      measurementData.handles.middle,
      {},
      interactionType,
      (success) => {
        measurementData.active = false;

        if (!success) {
          cornerstoneTools.removeToolState(
            element,
            this.toolId,
            measurementData
          );

          this.preventNewMeasurement = false;

          return;
        }

        measurementData.handles.end.active = true;

        cornerstone.updateImage(element);

        // Step 2, create end.
        Manipulators.moveNewHandle(
          eventData,
          this.toolId,
          measurementData,
          measurementData.handles.end,
          {},
          interactionType,
          (success) => {
            if (success) {
              measurementData.active = false;
              cornerstone.updateImage(element);
            } else {
              cornerstoneTools.removeToolState(
                element,
                this.toolId,
                measurementData
              );
            }

            this.preventNewMeasurement = false;
            cornerstone.updateImage(element);

            const modifiedEventData = {
              toolName: this.toolId,
              toolType: this.toolId, // Deprecation notice: toolType will be replaced by toolName
              element,
              measurementData,
            };

            triggerEvent(
              element,
              cornerstoneTools.EVENTS.MEASUREMENT_COMPLETED,
              modifiedEventData
            );
          }
        );
      }
    );
  }
}

function length(vector) {
  return Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));
}

function getSide(image, handleEnd, handleStart, imageMetaData: ImageMetadata) {
  const { rowPixelSpacing, colPixelSpacing } = getDimensionData(
    image,
    imageMetaData
  );

  return {
    x: (handleEnd.x - handleStart.x) * (colPixelSpacing || 1),
    y: (handleEnd.y - handleStart.y) * (rowPixelSpacing || 1),
  };
}
